import { useAppSelector } from "app/hooks";
import {
  Box,
  Card,
  CardHeader,
  Chip,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { CustomerApproval } from "models/Order";
import CheckCircle from "@mui/icons-material/CheckCircle";
import UserActivity from "components/UserActivity";
import { useState } from "react";
import { selectUserIsAdminOrUserAdmin } from "features/auth/authSlice";
import EditReasonDialog from "../dialogs/EditReasonDialog";
import EditOutlined from "@mui/icons-material/EditOutlined";
import House from "@mui/icons-material/House";
import { useGetSignatureByIdQuery } from "app/api/media";

type InspectionProps = {
  approval: CustomerApproval;
};

const Inspection: React.FC<InspectionProps> = (props) => {
  const { approval } = props;
  const userIsAdminOrUserAdmin = useAppSelector(selectUserIsAdminOrUserAdmin);
  const [showEditReasonDialog, setShowEditReasonDialog] = useState(false);
  const { data: signature } = useGetSignatureByIdQuery({ approvalId: approval.id });

  return (
    <Card
      variant="outlined"
      sx={(theme) => ({
        borderRadius: 3,
        borderColor: theme.palette.grey[400],
        bgcolor: approval.customerOnSite ? "background.success" : "background.disabled",
      })}
    >
      <CardHeader
        avatar={
          <CheckCircle fontSize="large" color={approval.customerOnSite ? "success" : "disabled"} />
        }
        title={
          <Typography fontWeight="bold">
            {approval.customerOnSite ? "Slutbesiktning med kund" : "Slutbesiktning utan kund"}
          </Typography>
        }
        subheader={
          <UserActivity
            user={{ userId: approval.owner.id, userName: approval.owner.userName }}
            time={approval.timestamp}
          />
        }
      />

      <Box
        sx={{
          px: 2,
          pb: 2,
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Stack spacing={1}>
            {approval.customerOnSite ? (
              <>
                <Typography>
                  <strong>Kund: </strong>
                  {approval.customerName}
                </Typography>

                <Box>
                  <Typography fontWeight="bold">Kunds kommentar:</Typography>
                  {approval.customerComment && <Typography>{approval.customerComment}</Typography>}
                </Box>

                <Box>
                  <Typography fontWeight="bold">
                    Godkända checklistor ({approval.approvedChecklists.length}/
                    {approval.noOfChecklists}
                    ):
                  </Typography>

                  {approval.approvedChecklists.length ? (
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {approval.approvedChecklists.map((approvedChecklist) => (
                        <Chip
                          key={approvedChecklist.id}
                          label={approvedChecklist.roomName}
                          icon={<House color="success" />}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography fontStyle="italic" color="disabled">
                      Inga checklistor godkända
                    </Typography>
                  )}
                </Box>
              </>
            ) : (
              <>
                <Typography>
                  <strong>Person som sökts: </strong>
                  {approval.triedToContactPerson}
                </Typography>

                <Typography>
                  <strong>Telefon: </strong>
                  {approval.triedToContactPhoneNumber}
                </Typography>

                <Typography>
                  <strong>Tid: </strong>
                  {approval.triedToContactAt}
                </Typography>

                <Box>
                  <Typography fontWeight="bold">
                    Kommentar:
                    {userIsAdminOrUserAdmin && (
                      <>
                        <Tooltip title="Redigera kommentar">
                          <IconButton
                            sx={{ my: -1, verticalAlign: "top" }}
                            onClick={() => setShowEditReasonDialog(true)}
                          >
                            <EditOutlined />
                          </IconButton>
                        </Tooltip>

                        {showEditReasonDialog && (
                          <EditReasonDialog
                            onClose={() => setShowEditReasonDialog(false)}
                            approval={approval}
                          />
                        )}
                      </>
                    )}
                  </Typography>

                  <Typography sx={{ whiteSpace: "break-spaces", overflowWrap: "anywhere" }}>
                    {approval.notOnSiteDescription}
                  </Typography>
                </Box>
              </>
            )}
          </Stack>
        </Box>

        {signature && (
          <Box>
            <Typography fontWeight="bold">Signatur:</Typography>
            <Link href={signature.imgUrl || ""} target="_blank">
              <Box
                component="img"
                src={signature?.imgUrl || ""}
                sx={(theme) => ({
                  maxHeight: 100,
                  aspectRatio: "1 / 1",
                  objectFit: "cover",
                  border: 1,
                  borderColor: theme.palette.grey[400],
                  borderRadius: 2,
                })}
              />
            </Link>
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default Inspection;
